import { Copy, Link as LinkIcon, Mail, Save } from "lucide-react";

import { GmailAccounts } from "@/components/GmailAccounts";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "@/api";

export default function GmailSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [secret, setSecret] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data));
    const params = new URLSearchParams(window.location.search);
    const err = params.get("gmail_error");
    const connected = params.get("gmail_connected");
    if (err || connected) {
      setTimeout(() => {
        if (err === "authorization_refused") toast.error("Autorisation Google refusée.");
        else if (err === "expired") toast.error("Le lien Google a expiré, relancez la connexion.");
        else if (err === "token_exchange") toast.error("Google a refusé l’échange du jeton (souvent : Client ID/Secret invalides).");
        else if (err === "missing_refresh_token") toast.error("Google n’a pas délivré de jeton d’envoi. Retirez l’accès Mairie Leads dans votre compte Google, puis reconnectez-vous.");
        if (connected === "1") toast.success("Compte Gmail ajouté avec succès.");
      }, 0);
      window.history.replaceState({}, "", "/gmail");
    }
  }, []);

  const callbackUrl = useMemo(() => `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/oauth/gmail/callback`, []);

  if (!settings) return <section className="page">Chargement Gmail…</section>;

  const save = async () => {
    try {
      const { data } = await api.put("/settings", {
        ...settings,
        gmail: { client_id: settings.gmail?.client_id || "", client_secret: secret || null },
      });
      setSettings(data);
      setSecret("");
      toast.success("Identifiants Gmail enregistrés de façon sécurisée.");
    } catch {
      toast.error("Les identifiants Gmail n’ont pas été enregistrés.");
    }
  };

  const connect = async () => {
    try {
      const { data } = await api.get("/oauth/gmail/start", { params: { callback_base_url: process.env.REACT_APP_BACKEND_URL } });
      window.location.assign(data.authorization_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "La connexion Google n’a pas pu démarrer.");
    }
  };

  const copyCallback = async () => {
    try {
      await navigator.clipboard.writeText(callbackUrl);
      toast.success("URL de callback copiée.");
    } catch {
      toast.error("Impossible de copier automatiquement, sélectionnez le texte à la main.");
    }
  };

  return (
    <section className="page" data-testid="gmail-settings-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">GMAIL</p>
          <h1>Connexion Google</h1>
          <p className="page-subtitle">Enregistrez les identifiants OAuth fournis par votre client.</p>
        </div>
        <button className="primary-button" onClick={save} data-testid="save-gmail-settings-button">
          <Save size={18} />Enregistrer
        </button>
      </header>
      <section className="settings-section">
        <div className="section-title">
          <Mail size={20} />
          <div><p className="eyebrow">OAUTH GOOGLE</p><h2>Identifiants sécurisés</h2></div>
        </div>
        <label>Google Client ID
          <input
            value={settings.gmail?.client_id || ""}
            onChange={(event) => setSettings({ ...settings, gmail: { ...settings.gmail, client_id: event.target.value } })}
            data-testid="gmail-client-id-input"
          />
        </label>
        <label>Google Client Secret
          <input
            type="password"
            value={secret}
            onChange={(event) => setSecret(event.target.value)}
            placeholder={settings.gmail?.configured ? "Secret enregistré — laissez vide pour conserver" : "Collez le secret ici"}
            data-testid="gmail-client-secret-input"
          />
        </label>

        <div className="callback-url-box" data-testid="gmail-callback-url-box">
          <p className="eyebrow">URL DE REDIRECTION À AUTORISER DANS GOOGLE CLOUD</p>
          <div className="callback-url-row">
            <code data-testid="gmail-callback-url">{callbackUrl}</code>
            <button className="secondary-button" type="button" onClick={copyCallback} data-testid="copy-callback-url-button">
              <Copy size={15} />Copier
            </button>
          </div>
          <p className="source-note">
            Ajoutez cette URL dans <em>Google Cloud Console → Identifiants → Client OAuth 2.0 → URI de redirection autorisés</em>, puis enregistrez. Sans cela, Google refuse la connexion et ne revient jamais sur l’application (aucun compte n’est ajouté).
          </p>
        </div>

        {settings.gmail?.last_error && <p className="review-alert" data-testid="gmail-last-error">{settings.gmail.last_error}</p>}
        <GmailAccounts configured={!!settings.gmail?.configured} onConnect={connect} />
      </section>
    </section>
  );
}
